import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps } from 'formik';
import classNames from 'classnames';

import TextWidget, {
  TextWidgetClassesType,
} from 'components/UI/Form/Widget/TextWidget/TextWidget';
import { TextRowType } from 'components/UI/Form/Row/TextRow/TextRow';
import { useField } from '../hooks/useField';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { FormikTextRowType } from './FormikTextRow';
import { PartialRecord } from 'types/Base';

import styles from './FormikDateInputsRow.module.scss';

type CustomClasses = {
  label?: string;
  errorMessage?: string;
  row?: string;
  inputsContainer?: string;
  day?: TextWidgetClassesType;
  month?: TextWidgetClassesType;
  year?: TextWidgetClassesType;
};

type AdditionalInputsProps = PartialRecord<
  'dayProps' | 'monthProps' | 'yearProps',
  Omit<
    Partial<TextRowType['inputProps']>,
    'onChange' | 'onBlur' | 'inputRef' | 'error' | 'type' | 'value'
  >
>;

type Props = Omit<TextRowType, 'value' | 'classes'> & {
  classes?: CustomClasses;
} & AdditionalInputsProps;

export default function FormikDateInputsRow({
  inputProps,
  label,
  classes,
  ...props
}: Props): JSX.Element {
  const { name } = inputProps;
  const { errorMessage, row, ...allClasses } = classes || {};
  const [field, { touched, error: formikError, apiError }] = useField(name);
  const error = formikError && touched ? formikError : apiError || undefined;

  return (
    <div className={row}>
      {label && <label className={classes?.label}>{label}</label>}
      <Field
        inputProps={{ ...inputProps, error: !!error }}
        {...props}
        field={field}
        classes={allClasses}
        component={DateInputFields}
      />
      <ErrorMessage name={name} additionalClass={errorMessage} />
    </div>
  );
}

export type ComponentProps = FormikTextRowType &
  FieldProps &
  Pick<
    Props,
    'inputProps' | 'dayProps' | 'monthProps' | 'yearProps' | 'classes'
  >;

const DateInputFields = ({
  form,
  field,
  inputProps,
  dayProps,
  monthProps,
  yearProps,
  classes,
}: ComponentProps) => {
  const { t } = useTranslation();
  const { disabled, error } = inputProps;
  const { name, value } = field;
  const { setFieldValue, setFieldTouched } = form;
  const values = value.toString().split('/');
  const [day, setDay] = useState<string>(values[0] || '');
  const [month, setMonth] = useState<string>(values[1] || '');
  const [year, setYear] = useState<string>(values[2] || '');

  const { placeholder: dPlaceholder, disabled: dDisabled, ...allDayProps } =
    dayProps || {};

  const { placeholder: mPlaceholder, disabled: mDisabled, ...allMonthProps } =
    monthProps || {};

  const { placeholder: yPlaceholder, disabled: yDisabled, ...allYearProps } =
    yearProps || {};

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  function convertToNumber(value: string) {
    return value.replace(/\D/g, '');
  }

  function convertToValidNumber(value: string) {
    if (value.length === 1 && value !== '0') {
      return '0' + value;
    }
    return value;
  }

  function validateFieldYear() {
    if (year.length < 4) {
      setFieldTouched(name, true);
    }
  }

  useEffect(() => {
    const date = `${day}/${month}/${year}`;
    setFieldValue(name, day || month || year ? date : '');
  }, [day, month, year, value]);

  return (
    <div className={classNames(styles.inputs, classes?.inputsContainer)}>
      <TextWidget
        type="text"
        value={day}
        maxLength={2}
        disabled={disabled ?? dDisabled ?? false}
        placeholder={dPlaceholder ?? t('LABEL_DAY')}
        onChange={e => setDay(convertToNumber(e.target.value))}
        onBlur={e => setDay(convertToValidNumber(e.target.value))}
        classes={{
          icons: styles.icons,
          ...classes?.day,
        }}
        inputRef={dayRef}
        error={error}
        {...allDayProps}
      />
      <TextWidget
        type="text"
        value={month}
        maxLength={2}
        disabled={disabled ?? mDisabled ?? false}
        placeholder={mPlaceholder ?? t('LABEL_MONTH')}
        onChange={e => setMonth(convertToNumber(e.target.value))}
        onBlur={e => setMonth(convertToValidNumber(e.target.value))}
        classes={{
          icons: styles.icons,
          ...classes?.month,
        }}
        inputRef={monthRef}
        error={error}
        {...allMonthProps}
      />
      <TextWidget
        type="text"
        value={year}
        minLength={4}
        disabled={disabled ?? yDisabled ?? false}
        maxLength={4}
        placeholder={yPlaceholder ?? t('LABEL_YEAR')}
        onChange={e => setYear(convertToNumber(e.target.value))}
        onBlur={validateFieldYear}
        classes={{
          icons: styles.icons,
          ...classes?.year,
        }}
        inputRef={yearRef}
        error={error}
        {...allYearProps}
      />
    </div>
  );
};
